import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Volume2, VolumeX, ChevronUp, ChevronDown, Loader2, ChevronRight } from 'lucide-react';
import Hls from 'hls.js';
import { useAirPlay } from '../utils/airplay';
import { AirPlayButton } from '../components/AirPlayButton';

const API = process.env.REACT_APP_BACKEND_URL;

// LiveChannel — the /live page. Everyone watching sees the same video at the
// same offset because the backend derives (video, offset) from wall-clock time
// modulo a stable running order. Client just:
//   1. Fetch /api/channel/now
//   2. Load hls_url into <video>, seek to offset_seconds, play
//   3. On ended / error → re-fetch (in case a video was added/removed
//      mid-loop, or we drifted through a duration boundary)
//   4. On tab wake (visibilitychange) → re-fetch to re-sync with the wheel
//
// The video is muted until the user taps once (browser autoplay policy).
// After that we remember the choice for the tab.

/**
 * Resolve legacy `aivue/…` object-storage paths to full backend URLs.
 * Bunny HLS URLs already start with https:// so they pass through unchanged.
 */
function resolveMediaUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = (API || '').replace(/\/+$/, '');
  return `${base}/api/files/${url.replace(/^\/+/, '')}`;
}

const formatClock = (seconds) => {
  const s = Math.max(0, Math.floor(seconds || 0));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
};

export default function LiveChannel() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(true);
  const [error, setError] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const [remainingLocal, setRemainingLocal] = useState(0);

  // Analytics session lifecycle (iter 121):
  //   • sessionIdRef  — the UUID the backend returned from /session/start.
  //     kept in a ref so unmount can read it even after state teardown.
  //   • currentVideoIdRef — the video the viewer is currently watching.
  //     read on every heartbeat + on unmount to stamp end_video_id.
  //   • qualifiedRef — set true at the 10s mark so the sendBeacon exit
  //     handler doesn't fire duplicate qualifies.
  const sessionIdRef = useRef(null);
  const currentVideoIdRef = useRef(null);
  const qualifiedRef = useRef(false);

  const airplay = useAirPlay(videoRef, data?.now?.id);

  // Per-viewer independent rotation (iter 122). Instead of the wall-clock
  // /now → same-video-for-everyone approach, each viewer fetches the full
  // schedule once, picks a random starting index, and advances through it
  // locally on `ended`. Every fresh page load = different starting point
  // + a Fisher-Yates shuffle of the full loop = maximum catalog exposure
  // for solo viewers. Trade-off: no "everyone in sync" property (nobody
  // was using it), and no server round-trip per rollover (faster).
  const playlistRef = useRef([]);        // Fisher-Yates-shuffled full loop
  const playlistIndexRef = useRef(0);    // where in the shuffle we are
  const advancingRef = useRef(false);    // guard against double-fires

  const buildLocalPlaylist = useCallback(async () => {
    // Ask for a big enough window that the schedule wraps the full loop
    // at least once. 24h > any realistic loop → we get every video once.
    const r = await fetch(`${API}/api/channel/schedule?hours=24`, { cache: 'no-store' });
    if (!r.ok) throw new Error(`schedule HTTP ${r.status}`);
    const j = await r.json();
    const items = (j?.items || []).filter((it) => it && it.id && it.hls_url);
    // De-dup by id — schedule wraps the loop so the same video may appear
    // twice. First-occurrence wins (preserves original order stability
    // before we shuffle it below).
    const seen = new Set();
    const unique = [];
    for (const it of items) {
      if (seen.has(it.id)) continue;
      seen.add(it.id);
      unique.push(it);
    }
    // Fisher-Yates shuffle — independent per viewer, per page load.
    for (let i = unique.length - 1; i > 0; i--) {
      const j2 = Math.floor(Math.random() * (i + 1));
      [unique[i], unique[j2]] = [unique[j2], unique[i]];
    }
    return unique;
  }, []);

  // Load an item from the local shuffled playlist into the <video>.
  // Starts each video at offset 0 (no jarring mid-song joins on rollover).
  const loadCurrentItem = useCallback(() => {
    const list = playlistRef.current;
    if (!list.length) return;
    const idx = playlistIndexRef.current % list.length;
    const item = list[idx];
    const v = videoRef.current;
    if (!v || !item) return;

    // Present same shape as /now for the overlay UI + analytics heartbeat.
    setData({
      live: true,
      now: item,
      offset_seconds: 0,
      remaining_seconds: item.duration || 0,
      next: [list[(idx + 1) % list.length], list[(idx + 2) % list.length], list[(idx + 3) % list.length]].filter(Boolean),
      channel: { videos: list.length, loop_seconds: list.reduce((s, x) => s + (x.duration || 0), 0), loop_hours: 0 },
    });
    setRemainingLocal(item.duration || 0);
    setError('');
    setLoading(false);

    const url = resolveMediaUrl(item.hls_url);
    if (!url) return;
    try { hlsRef.current?.destroy?.(); } catch (_e) { /* teardown safety */ }
    hlsRef.current = null;

    const isHls = /\.m3u8($|\?)/i.test(url);
    const nativeHls = v.canPlayType('application/vnd.apple.mpegurl') !== '';
    if (isHls && !nativeHls && Hls.isSupported()) {
      const hls = new Hls({ maxBufferLength: 20 });
      hls.loadSource(url);
      hls.attachMedia(v);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        try { v.currentTime = 0; } catch (_e) { /* seek pre-metadata */ }
        v.play().catch(() => { /* autoplay blocked while muted is fine */ });
      });
      hlsRef.current = hls;
    } else {
      v.src = url;
      const onMeta = () => {
        try { v.currentTime = 0; } catch (_e) { /* seek pre-metadata */ }
        v.play().catch(() => { /* autoplay blocked while muted is fine */ });
        v.removeEventListener('loadedmetadata', onMeta);
      };
      v.addEventListener('loadedmetadata', onMeta);
    }
  }, []);

  // Advance to the next item on `ended`. Also called by `error` to skip
  // past a video that can't play. `advancingRef` prevents both handlers
  // from firing back-to-back on the same rollover.
  const advanceToNext = useCallback(() => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setTimeout(() => { advancingRef.current = false; }, 1500);
    playlistIndexRef.current = (playlistIndexRef.current + 1) % Math.max(1, playlistRef.current.length);
    loadCurrentItem();
  }, [loadCurrentItem]);

  // syncFromServer is now a fallback for the empty-channel / error case
  // AND the initial mount trigger. It fetches the schedule, shuffles it
  // per-viewer, picks a random start index, and loads that item.
  const syncFromServer = useCallback(async () => {
    try {
      const list = await buildLocalPlaylist();
      if (!list.length) {
        setError('No videos in the channel yet.');
        setLoading(false);
        return;
      }
      playlistRef.current = list;
      // Random start position — the whole reason for this iteration.
      // Different every mount, different for every viewer.
      playlistIndexRef.current = Math.floor(Math.random() * list.length);
      loadCurrentItem();
    } catch (e) {
      setError(e.message || 'Channel unavailable');
      setLoading(false);
    }
  }, [buildLocalPlaylist, loadCurrentItem]);

  // Initial mount: build the local shuffle, pick a random start. NO
  // visibilitychange re-sync — that would reshuffle the whole playlist
  // when the user switched tabs and came back, throwing away their
  // position for no benefit (per-viewer independent rotation, iter 122).
  useEffect(() => {
    syncFromServer();
  }, [syncFromServer]);

  // Cleanup HLS on unmount.
  useEffect(() => () => { try { hlsRef.current?.destroy?.(); } catch (_e) { /* unmount teardown */ } }, []);

  // Local countdown ticker for the "remaining" chip. Pauses when muted-
  // paused or when we're between videos.
  useEffect(() => {
    if (!data?.live || !data?.now?.id) return undefined;
    const t = setInterval(() => setRemainingLocal((r) => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [data?.live, data?.now?.id]);

  // Keep currentVideoIdRef in sync with whatever /now returned.
  useEffect(() => {
    if (data?.now?.id) currentVideoIdRef.current = data.now.id;
  }, [data?.now?.id]);

  // Analytics session lifecycle. All three fetches are fire-and-forget —
  // any failure must not break playback. On unmount we use sendBeacon so
  // the end event survives navigation to another page.
  useEffect(() => {
    let qualifyTimer = null;
    let heartbeatTimer = null;
    let cancelled = false;

    // (1) Open the session. Server records IP, UA, referrer source, and
    // returns the UUID we tag all subsequent events with. The `src=banner`
    // query param is set by LiveBanner's navigate call; direct visits and
    // deep links have no src, so the server buckets them by referrer.
    const src = searchParams.get('src') || undefined;
    const referrer = (typeof document !== 'undefined' && document.referrer) || undefined;
    fetch(`${API}/api/channel/session/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ src, referrer, current_video_id: currentVideoIdRef.current }),
    })
      .then((r) => r.ok ? r.json() : null)
      .then((j) => {
        if (cancelled || !j?.session_id) return;
        sessionIdRef.current = j.session_id;

        // (2) Qualify after 10 s on-page. This is what "unique viewer"
        // counts read. Bounces (<10 s) are deliberately not counted.
        qualifyTimer = setTimeout(() => {
          if (!sessionIdRef.current || qualifiedRef.current) return;
          qualifiedRef.current = true;
          fetch(`${API}/api/channel/session/qualify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ session_id: sessionIdRef.current }),
            keepalive: true,
          }).catch(() => { /* fire-and-forget */ });
        }, 10_000);

        // (3) Heartbeat every 15 s so last_seen_at stays fresh (the
        // background reaper closes sessions idle >5 min) and the
        // videos_seen array picks up whatever's currently on screen.
        heartbeatTimer = setInterval(() => {
          if (!sessionIdRef.current) return;
          fetch(`${API}/api/channel/session/heartbeat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              session_id: sessionIdRef.current,
              current_video_id: currentVideoIdRef.current || undefined,
            }),
            keepalive: true,
          }).catch(() => { /* fire-and-forget */ });
        }, 15_000);
      })
      .catch(() => { /* analytics may not block playback */ });

    // (4) End the session on unmount / pagehide. sendBeacon guarantees
    // delivery during navigation — a plain fetch would be aborted by the
    // browser when the page transitions. The Blob body is required
    // because Beacon can't set arbitrary Content-Type headers.
    const endSession = () => {
      if (!sessionIdRef.current) return;
      const body = JSON.stringify({
        session_id: sessionIdRef.current,
        end_video_id: currentVideoIdRef.current || undefined,
      });
      try {
        const blob = new Blob([body], { type: 'application/json' });
        navigator.sendBeacon(`${API}/api/channel/session/end`, blob);
      } catch (_e) {
        // Fallback for browsers without sendBeacon — best-effort fetch.
        fetch(`${API}/api/channel/session/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => { /* teardown best-effort */ });
      }
      sessionIdRef.current = null;
    };
    window.addEventListener('pagehide', endSession);
    return () => {
      cancelled = true;
      if (qualifyTimer) clearTimeout(qualifyTimer);
      if (heartbeatTimer) clearInterval(heartbeatTimer);
      window.removeEventListener('pagehide', endSession);
      endSession();
    };
    // Intentionally empty deps — session lifecycle spans the entire
    // page lifetime, not per-video. searchParams is captured once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sound toggle — first user interaction on the video counts as consent to
  // unmute, per browser autoplay rules.
  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    const next = !muted;
    v.muted = next;
    setMuted(next);
    if (!next) v.play().catch(() => { /* user click gives us the gesture; some engines still refuse */ });
  };

  const upNext = data?.next || [];
  const wireless = airplay.wireless;

  return (
    <div className="fixed inset-0 z-[70] bg-black text-white flex flex-col" data-testid="live-channel-page">
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-contain bg-black"
        playsInline
        autoPlay
        muted={muted}
        onEnded={advanceToNext}
        onError={advanceToNext}
        x-webkit-airplay="allow"
        data-testid="live-channel-video"
      />

      {/* Loading / empty overlay */}
      {(loading || error) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 z-10">
          {loading && !error && <Loader2 className="w-8 h-8 text-white/70 animate-spin" />}
          {error && (
            <div className="text-center px-6" data-testid="live-channel-empty">
              <div className="text-lg font-semibold mb-2">Channel unavailable</div>
              <div className="text-sm text-white/60 mb-4">{error}</div>
              <button
                onClick={syncFromServer}
                className="px-4 py-2 rounded-full bg-white text-black text-sm font-medium hover:bg-zinc-200"
                data-testid="live-channel-retry"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      )}

      {/* Top bar — back + LIVE badge + AirPlay + mute */}
      <div
        className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-4 pb-3 bg-gradient-to-b from-black/80 to-transparent"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center hover:bg-black/70"
          data-testid="live-channel-back"
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600/30 border border-red-500/40" data-testid="live-channel-badge">
          <span className="relative flex w-2 h-2">
            <span className="animate-ping absolute inline-flex w-full h-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex w-2 h-2 rounded-full bg-red-500"></span>
          </span>
          <span className="text-[11px] font-bold tracking-widest text-red-100">LIVE</span>
          {wireless && (
            <span className="text-[10px] font-medium text-fuchsia-200 pl-2 border-l border-red-500/40">AirPlay</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {data?.now && !data.now.is_lyric && (
            <AirPlayButton
              videoRef={videoRef}
              videoKey={data.now.id}
              size={18}
              className="w-10 h-10"
              data-testid="live-channel-airplay"
            />
          )}
          <button
            onClick={toggleSound}
            className="w-10 h-10 rounded-full bg-black/50 backdrop-blur flex items-center justify-center hover:bg-black/70"
            data-testid="live-channel-sound-toggle"
            aria-label={muted ? 'Unmute' : 'Mute'}
          >
            {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Big "Tap for sound" bubble — visible only while muted so users don't
          miss that they need to opt in to audio. */}
      {muted && !loading && !error && (
        <button
          onClick={toggleSound}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 hover:bg-white/20 transition-colors"
          data-testid="live-channel-tap-for-sound"
        >
          <VolumeX className="w-5 h-5" />
          <span className="text-sm font-medium">Tap for sound</span>
        </button>
      )}

      {/* Bottom info panel — now playing + up next expander */}
      {data?.now && (
        <div
          className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/90 to-transparent pt-16 pb-4 px-4"
          style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))' }}
        >
          {/* Progress bar — client-side tick so it feels smooth without
              polling the server every second. Server /now provides the
              anchor; the local timer just decrements remaining. */}
          <div className="h-1 rounded-full bg-white/20 mb-3 overflow-hidden">
            <div
              className="h-full bg-red-500 transition-[width] duration-1000 ease-linear"
              style={{
                width: `${Math.max(0, Math.min(100, ((data.now.duration - remainingLocal) / Math.max(1, data.now.duration)) * 100))}%`,
              }}
            />
          </div>

          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-xs text-white/60 mb-0.5">Now Playing</div>
              <h2 className="text-lg font-bold truncate" data-testid="live-channel-title">{data.now.title}</h2>
              {/* Artist row — link when we have an artist_id, plain text
                  otherwise. The chevron gives a visual affordance that it
                  navigates. Ellipsis-truncation keeps this on one line on
                  narrow screens. */}
              {data.now.artist_id ? (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/creator/${data.now.artist_id}`); }}
                  className="group inline-flex items-center gap-0.5 max-w-full text-sm text-white/70 hover:text-white transition-colors truncate"
                  data-testid="live-channel-artist-link"
                  aria-label={`View artist ${data.now.artist}`}
                >
                  <span className="truncate">{data.now.artist}</span>
                  <ChevronRight className="w-3.5 h-3.5 flex-shrink-0 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                </button>
              ) : (
                <div className="text-sm text-white/70 truncate">{data.now.artist}</div>
              )}
              {/* "View artist" pill — TV-friendly hit target. Only rendered
                  when we have an artist_id to link to, per spec. */}
              {data.now.artist_id && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/creator/${data.now.artist_id}`); }}
                  className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full border border-white/20 text-xs text-white/80 hover:text-white hover:border-white/40 hover:bg-white/5 transition-colors"
                  data-testid="live-channel-view-artist-btn"
                >
                  View artist
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              )}
              <div className="text-[11px] text-white/50 mt-1">
                {formatClock(data.now.duration - remainingLocal)} / {formatClock(data.now.duration)}
                {data?.channel?.videos > 0 && (
                  <> · {data.channel.videos} videos on rotation</>
                )}
              </div>
            </div>

            <button
              onClick={() => setShowGuide((v) => !v)}
              className="flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 text-xs font-medium"
              data-testid="live-channel-guide-toggle"
            >
              Up Next
              {showGuide ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
          </div>

          <AnimatePresence>
            {showGuide && upNext.length > 0 && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-3 overflow-hidden"
                data-testid="live-channel-upnext"
              >
                <div className="space-y-2 pt-2 border-t border-white/10">
                  {upNext.map((n, i) => (
                    <div
                      key={n.id}
                      className="flex items-center gap-3"
                      data-testid={`live-channel-upnext-${i}`}
                    >
                      <div className="w-14 h-14 rounded-md overflow-hidden bg-zinc-800 flex-shrink-0">
                        {n.thumbnail_url ? (
                          <img
                            src={resolveMediaUrl(n.thumbnail_url)}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          />
                        ) : null}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium truncate">{n.title}</div>
                        {/* Artist link per row. Falls back to plain span
                            when artist_id is missing so the row stays
                            visually consistent. */}
                        {n.artist_id ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/creator/${n.artist_id}`); }}
                            className="text-xs text-white/60 hover:text-white transition-colors truncate max-w-full text-left"
                            data-testid={`live-channel-upnext-artist-${i}`}
                            aria-label={`View artist ${n.artist}`}
                          >
                            {n.artist}
                          </button>
                        ) : (
                          <div className="text-xs text-white/60 truncate">{n.artist}</div>
                        )}
                      </div>
                      <div className="text-[11px] text-white/40 flex-shrink-0">{formatClock(n.duration)}</div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
